import { functions } from '@/lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { useEffect, useState } from "react";
import appBanner from "@/assets/welcome-screen.gif";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Smartphone,
  BarChart3,
  IndianRupee,
  ShieldCheck,
  Zap,
  Star,
  Download,
  CheckCircle2,
  Users,
  Bell,
  X,
  Menu,
} from "lucide-react";
import { Link } from "wouter";

const PLAY_STORE_URL = "https://play.google.com/store/apps/details?id=com.asldigitalsolutions.dailyhisaab";

// Public header nav. No login/sign-up entry — the website does not offer web login/sign-up.
// "Features" scrolls to the on-page section; the rest are existing public routes.
const NAV = [
  { label: "Features", href: "#features", type: "anchor" as const },
  { label: "Downloads", href: "/downloads", type: "route" as const },
  { label: "Docs", href: "/docs", type: "route" as const },
  { label: "Contact", href: "/support", type: "route" as const },
];

const features = [
  {
    icon: Smartphone,
    title: "Tap & Bill Instantly",
    description:
      "Select products with a single tap, adjust quantity, and generate bills in seconds. No complicated menus.",
  },
  {
    icon: BarChart3,
    title: "Real-Time Dashboard",
    description:
      "Track daily, weekly, monthly & yearly sales with beautiful charts. Know your numbers at a glance.",
  },
  {
    icon: IndianRupee,
    title: "GST Invoice Ready",
    description:
      "Generate professional GST invoices with auto-numbering. Share instantly via WhatsApp or QR code.",
  },
  {
    icon: ShieldCheck,
    title: "Secure Cloud Backup",
    description:
      "Your data is securely stored in the cloud. Optional Google Sheets backup keeps you in full control.",
  },
  {
    icon: Bell,
    title: "Smart Alerts",
    description:
      "Get notified about low stock, daily sales summaries, and important business events automatically.",
  },
  {
    icon: Users,
    title: "Built for Indian SMBs",
    description:
      "Designed specifically for Indian small businesses — kirana stores, restaurants, retailers & more.",
  },
];

const steps = [
  {
    icon: Download,
    title: "Download the App",
    description: "Get DailyHisaab free from the Google Play Store.",
  },
  {
    icon: Smartphone,
    title: "Sign Up with Phone",
    description: "Quick OTP verification — no email or password needed.",
  },
  {
    icon: Zap,
    title: "Start Billing",
    description: "Add your products and start billing customers instantly.",
  },
];

const testimonials = [
  {
    name: "Rajesh K.",
    business: "Kirana Store, Delhi",
    text: "DailyHisaab made my billing so simple. I just tap products and the bill is ready. Saved me hours every week!",
    rating: 5,
  },
  {
    name: "Priya S.",
    business: "Bakery, Kolkata",
    text: "The dashboard helps me see my daily and weekly sales clearly. Best billing app for small businesses.",
    rating: 5,
  },
  {
    name: "Amit P.",
    business: "Electronics Shop, Mumbai",
    text: "GST invoices are generated automatically. I share them on WhatsApp directly. Very professional!",
    rating: 5,
  },
];

const highlights = [
  { value: "10,000+", label: "Active Businesses" },
  { value: "50L+", label: "Bills Generated" },
  { value: "4.8", label: "Play Store Rating" },
  { value: "100%", label: "Free to Start" },
];

function GooglePlayBadge() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 135 40"
      className="h-[40px] w-auto"
    >
      <path
        d="M130 0H5C2.24 0 0 2.24 0 5v30c0 2.76 2.24 5 5 5h125c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5z"
        fill="#a6a6a6"
      />
      <path
        d="M130 .8c2.32 0 4.2 1.88 4.2 4.2v30c0 2.32-1.88 4.2-4.2 4.2H5C2.68 39.2.8 37.32.8 35V5C.8 2.68 2.68.8 5 .8h125m0-.8H5C2.24 0 0 2.24 0 5v30c0 2.76 2.24 5 5 5h125c2.76 0 5-2.24 5-5V5c0-2.76-2.24-5-5-5z"
        fill="#000000"
        opacity=".1"
      />
      <path
        d="M130 .8c2.32 0 4.2 1.88 4.2 4.2v30c0 2.32-1.88 4.2-4.2 4.2H5C2.68 39.2.8 37.32.8 35V5C.8 2.68 2.68.8 5 .8h125"
        fill="#000"
      />
      <text
        x="47.5"
        y="10.2"
        fontFamily="'Roboto',sans-serif"
        fontSize="6.5"
        fill="#fff"
        letterSpacing=".7"
      >
        GET IT ON
      </text>
      <text
        x="47.5"
        y="26"
        fontFamily="'Roboto',sans-serif"
        fontSize="13"
        fill="#fff"
        fontWeight="500"
      >
        Google Play
      </text>
      <path
        d="M20.44 7.54c-.16.18-.25.45-.25.79v23.35c0 .34.09.61.26.78l.04.04 13.08-13.08v-.31L20.48 7.5l-.04.04z"
        fill="#4285f4"
      />
      <path
        d="M37.93 23.78l-4.36-4.36v-.31-.31l4.36-4.36.1.06 5.16 2.93c1.48.84 1.48 2.21 0 3.05l-5.16 2.93-.1.06v.31z"
        fill="#ffbc00"
      />
      <path
        d="M38.03 23.47L33.57 19 20.44 32.13c.49.51 1.29.58 2.19.06l15.4-8.72"
        fill="#f04336"
      />
      <path
        d="M38.03 14.53L22.63 5.81c-.9-.51-1.7-.45-2.19.07L33.57 19l4.46-4.47z"
        fill="#31aa52"
      />
    </svg>
  );
}

function PlayStoreButton({
  className = "",
  onClick,
}: {
  className?: string;
  onClick?: () => void;
}) {
  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`inline-block ${className}`}
        data-testid="link-play-store"
      >
        <GooglePlayBadge />
      </button>
    );
  }
  return (
    <a
      href={PLAY_STORE_URL}
      target="_blank"
      rel="noopener noreferrer"
      className={`inline-block ${className}`}
      data-testid="link-play-store"
    >
      <GooglePlayBadge />
    </a>
  );
}

export default function MarketingLanding() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const prevTitle = document.title;
    document.title =
      "DailyHisaab - Simple Billing App for Indian Businesses | Download Free";

    const setMeta = (name: string, content: string, attr = "name") => {
      let el = document.querySelector(
        `meta[${attr}="${name}"]`,
      ) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };

    setMeta(
      "description",
      "DailyHisaab is the simplest billing app for Indian small businesses. Tap & bill, track sales, generate GST invoices, and backup to Google Sheets. Download free on Play Store.",
    );
    setMeta(
      "og:title",
      "DailyHisaab - Simple Billing App for Indian Businesses",
      "property",
    );
    setMeta(
      "og:description",
      "Tap products, generate bills, track sales, and send GST invoices — all from your phone. Free for Indian businesses.",
      "property",
    );
    setMeta("og:type", "website", "property");
    setMeta("og:url", "https://dailyhisaab.com/en-in", "property");

    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <Logo size="md" />

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-6">
            {NAV.map((item) =>
              item.type === "anchor" ? (
                <a
                  key={item.label}
                  href={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ) : (
                <Link
                  key={item.label}
                  href={item.href}
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              variant="default"
              size="sm"
              className="gap-1.5"
              onClick={() => window.open(PLAY_STORE_URL, "_blank", "noopener,noreferrer")}
              data-testid="link-header-download"
            >
              <Download className="h-4 w-4" />
              <span className="hidden sm:inline">Get the App</span>
              <span className="sm:hidden">Download</span>
            </Button>
            {/* Mobile hamburger */}
            <button
              className="md:hidden inline-flex items-center justify-center h-9 w-9 rounded-lg text-foreground hover:bg-muted transition-colors"
              onClick={() => setMobileMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-expanded={mobileMenuOpen}
              data-testid="button-mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile dropdown nav */}
        {mobileMenuOpen && (
          <nav className="md:hidden border-t border-border bg-background">
            <div className="container mx-auto px-4 py-1 flex flex-col">
              {NAV.map((item) =>
                item.type === "anchor" ? (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3 text-sm font-medium text-foreground hover:text-primary transition-colors border-b border-border last:border-0"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.label}
                    href={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="py-3 text-sm font-medium text-foreground hover:text-primary transition-colors border-b border-border last:border-0"
                  >
                    {item.label}
                  </Link>
                ),
              )}
            </div>
          </nav>
        )}
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-background to-primary/4" />
        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div
              className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-medium mb-6"
              data-testid="badge-india"
            >
              MADE WITH ❤️ IN BENGAL 🇮🇳
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight text-foreground">
              The Simplest Billing App
              <br />
              <span className="text-primary">for Indian Businesses</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              Tap products, generate bills, track sales, and send GST invoices —
              all from your phone. No training needed. Start billing in under 2
              minutes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-6">
              <PlayStoreButton />
            </div>
          </div>

          <div className="mt-16 md:mt-20 flex justify-center">
            <img
              src={appBanner} // Make sure this does NOT have quotes around it
              alt="DailyHisaab App"
              className="w-full max-w-2xl"
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-primary/5 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 max-w-4xl mx-auto">
            {highlights.map((stat) => (
              <div
                key={stat.label}
                className="text-center"
                data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <p className="text-3xl md:text-4xl font-bold text-primary">
                  {stat.value}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="scroll-mt-20 py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Everything Your Business Needs
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              Powerful features designed for busy business owners who want
              results, not complexity.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((feature) => (
              <Card
                key={feature.title}
                className="bg-card border-border hover:shadow-md transition-shadow"
                data-testid={`card-feature-${feature.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-lg font-semibold mb-2 text-foreground">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Start Billing in 3 Easy Steps
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              No training, no setup fees. Get started in under 2 minutes.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {steps.map((step, i) => (
              <div
                key={step.title}
                className="text-center relative"
                data-testid={`step-${i + 1}`}
              >
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <step.icon className="h-7 w-7" />
                </div>
                <div className="absolute top-8 left-[calc(50%+40px)] w-[calc(100%-80px)] h-px bg-border hidden md:block last:hidden" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">
                  {step.title}
                </h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Trusted by Business Owners
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto text-lg">
              See what Indian entrepreneurs are saying about DailyHisaab.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {testimonials.map((t) => (
              <Card
                key={t.name}
                className="bg-card border-border"
                data-testid={`card-testimonial-${t.name.toLowerCase().replace(/[\s.]/g, "-")}`}
              >
                <CardContent className="p-6">
                  <div className="flex gap-0.5 mb-3">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star
                        key={i}
                        className="h-4 w-4 fill-primary text-primary"
                      />
                    ))}
                  </div>
                  <p className="text-foreground mb-4 leading-relaxed">
                    "{t.text}"
                  </p>
                  <div>
                    <p className="font-semibold text-foreground text-sm">
                      {t.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {t.business}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Why Choose DailyHisaab?
            </h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-4">
            {[
              "100% free to start — no hidden charges",
              "Works offline — bill even without internet",
              "GST & non-GST invoicing with WhatsApp sharing",
              "Real-time sales analytics & smart dashboard",
              "Barcode scanner for quick product entry",
              "Optional Google Sheets backup — you own your data",
              "Lightweight app — works on any Android phone",
              "Designed specifically for Indian businesses",
            ].map((item) => (
              <div
                key={item}
                className="flex items-start gap-3 bg-card rounded-lg p-4 border border-border"
                data-testid="item-benefit"
              >
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <span className="text-foreground">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-20 bg-primary">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary-foreground">
            Ready to Simplify Your Billing?
          </h2>
          <p className="text-primary-foreground/80 mb-8 max-w-xl mx-auto text-lg">
            Join thousands of Indian business owners who trust DailyHisaab for
            their daily operations.
          </p>
        </div>
      </section>

      <footer className="border-t border-border py-8">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center gap-6 text-center">
            <Logo size="sm" />

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <Link
                href="/privacy"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Privacy Policy
              </Link>
              <Link
                href="/terms"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Terms of Service
              </Link>
              <Link
                href="/data-deletion"
                className="hover:text-primary transition-colors whitespace-nowrap"
              >
                Data Deletion Policy
              </Link>
            </div>

            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">
                Made with ❤️ in Bengal by{" "}
                <a
                  href="https://asldigitalsolutions.in"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary font-semibold hover:underline"
                >
                  ASL Digital Solutions
                </a>
              </p>
              <p className="text-sm text-muted-foreground">
                &copy;2026 DailyHisaab. All Rights Reserved
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
